import { guards } from '@shared/helpers/typeguards.helpers';

const PDF_RENDERING_CANCELLED = 'RenderingCancelledException';

/**
 * pdf.js page proxy surface needed to release raster resources.
 */
export type PdfPageWithCleanup = {
  cleanup: () => void;
};

/**
 * pdf.js `name` on {@link Error} or a plain `{ name }` object (tests / duck-typed rejections).
 */
const pdfRenderErrorName = (error: unknown): string | undefined => {
  if (guards.is.error(error)) return error.name;
  if (!guards.is.object(error) || !guards.is.string(error.name))
    return undefined;
  return error.name;
};

/**
 * True when pdf.js aborted a paint (new render, unmount, or {@link PDFPageProxy.cleanup}).
 */
export const isPdfRenderingCancelled = (error: unknown): boolean =>
  pdfRenderErrorName(error) === PDF_RENDERING_CANCELLED;

/**
 * Swallow {@link isPdfRenderingCancelled}; log any other rejection so `void` chains stay visible.
 */
export const reportUnhandledPdfRenderError = (error: unknown): void => {
  if (isPdfRenderingCancelled(error)) return;
  console.error(error);
};

/** pdf.js render-task surface used when cancelling a paint. */
export type CancellablePdfRenderTask = {
  cancel: () => void;
  promise: Promise<unknown>;
};

/**
 * Cancel an in-flight pdf.js paint and swallow {@link isPdfRenderingCancelled}.
 * `task.promise` rejects on cancel; leaving it unhandled shows as a console error.
 */
export const cancelPdfRenderTask = (task: CancellablePdfRenderTask): void => {
  task.cancel();
  void task.promise.catch(reportUnhandledPdfRenderError);
};

/**
 * Run work against a pdf.js page proxy and always call {@link PdfPageWithCleanup.cleanup}.
 */
export const runWithPdfPage = async <T, P extends PdfPageWithCleanup>(
  page: P,
  run: (page: P) => Promise<T>,
): Promise<T> => {
  try {
    return await run(page);
  } finally {
    page.cleanup();
  }
};
